import { Injectable } from '@angular/core';
import { addressString } from '@core/util/address';
import {
	clientLegalName,
	getSignatories,
	isGuarantor,
} from '@core/util/client';
import { DateToShortDate } from '@core/util/datetime';
import { getPrimaryParcel } from '@core/util/parcels';
import { isSet } from '@core/util/value';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { Business, BusinessAddress } from '@models/businesses';
import { ClientType, CloserIdentificationTypes } from '@models/clients';
import { IdentificationPriority } from '@models/identification';
import {
	BusinessMatterClient,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	ClientsCapacity,
	Matter,
	MatterTitle,
	PersonMatterClient,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { MatterMortgage } from '@models/mortgage';
import { getIdentification } from '@state/global/tags/mappings/tags.identification';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { format } from 'date-fns';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from '../../base-form-mapper';
import { joinNames } from '@core/util/string';

@Injectable({
	providedIn: 'root',
})
export class TD_NON_FACE_TO_FACE_ID_Mapper extends BaseFormMapper<TD_NON_FACE_TO_FACE_ID> {
	CloserToFormMappings = Mappings;
	SecondaryIdTypesToDetect = [
		CloserIdentificationTypes.UTILITY_BILL,
		CloserIdentificationTypes.BANK_STATEMENT_TD,
		CloserIdentificationTypes.BANK_STATEMENT_NON_TD,
		CloserIdentificationTypes.CREDIT_CARD_STATEMENT_TD,
		CloserIdentificationTypes.CREDIT_CARD_STATEMENT_NON_TD,
		CloserIdentificationTypes.BENEFTIS_STATEMENT,
		CloserIdentificationTypes.LINE_OF_CREDIT_STATEMENT_TD,
		CloserIdentificationTypes.LINE_OF_CREDIT_STATEMENT_NON_TD,
		CloserIdentificationTypes.LOAN_ACCOUNT_STATEMENT_TD,
		CloserIdentificationTypes.LOAN_ACCOUNT_STATEMENT_NON_TD,
		CloserIdentificationTypes.MORTGAGE_STATEMENT_TD,
		CloserIdentificationTypes.MORTGAGE_STATEMENT_NON_TD,
		CloserIdentificationTypes.PROPERTY_TAX_ASSESSMENT_MUNICIPAL,
		CloserIdentificationTypes.CCP_CONTRIBUTION_STATEMENT,
		CloserIdentificationTypes.INSURANCE_DOCUMENTS,
		CloserIdentificationTypes.REGISTERED_ACCOUNT_STATEMENT_TD,
		CloserIdentificationTypes.REGISTERED_ACCOUNT_STATEMENT_NON_TD,
		CloserIdentificationTypes.RRSP_STATEMENT_NON_TD,
		CloserIdentificationTypes.T4_STATEMENT,
		CloserIdentificationTypes.CRA_DOCUMENTS,
	];

	mergeFormData(
		formData: TD_NON_FACE_TO_FACE_ID,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
		clientsCapacity: ClientsCapacity,
		mortgage: MatterMortgage,
	): TD_NON_FACE_TO_FACE_ID {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as TD_NON_FACE_TO_FACE_ID);
		const filteredMatterClients = matterClients.filter(
			(x) => !isGuarantor(x) || mortgage.guarantors.includes(x.id),
		);

		if (!isSet(_formData?.fax)) {
			_formData.fax = '1-855-522-2755';
		}

		if (!isSet(_formData?.email)) {
			_formData.email = 'brokerfinalreports@td.com';
		}

		if (businessAddress?.cellPhone && !isSet(_formData?.solicitorTelephone)) {
			_formData.solicitorTelephone = businessAddress.phoneNumber;
		}

		if (businessAddress?.faxNumber && !isSet(_formData?.solicitorFax)) {
			_formData.solicitorFax = businessAddress.faxNumber;
		}

		if (businessAddress?.email && !isSet(_formData?.solicitorEmail)) {
			_formData.solicitorEmail = businessAddress.email;
		}

		if (!isSet(_formData?.date)) {
			_formData.date = format(new Date(), 'MMMM dd, yyyy');
		}

		if (mortgage?.referenceNumber && !isSet(_formData?.flexlineAccountNumber)) {
			_formData.flexlineAccountNumber = mortgage.referenceNumber;
		}

		if (matterClients?.length && !isSet(_formData?.mortgagors)) {
			_formData.mortgagors = joinNames(
				matterClients
					.filter((x) => !isGuarantor(x))
					.map((client) =>
						clientLegalName(client, nameFormattingSettings, {
							applyFormatting: false,
						}),
					),
			);
		}

		if (mortgage.guarantors?.length && !isSet(_formData?.guarantors)) {
			_formData.guarantors = joinNames(
				matterClients
					.filter(
						(client) =>
							isGuarantor(client) && mortgage.guarantors.includes(client.id),
					)
					.map((client) =>
						clientLegalName(client, nameFormattingSettings, {
							applyFormatting: false,
						}),
					),
			);
		}

		if (
			matter?.propertyDetails?.address?.street &&
			!isSet(_formData?.propertyAddress)
		) {
			_formData.propertyAddress = addressString(matter.propertyDetails.address);
		}

		if (matterTitles && !isSet(_formData?.propertyDescription)) {
			_formData.propertyDescription =
				getPrimaryParcel(matterTitles)?.description;
		}

		if (matter?.lawyerAssigned && !isSet(_formData?.solicitorName)) {
			_formData.solicitorName = matter.lawyerAssigned.name;
		}

		if (!isSet(_formData?.solicitorSignDate)) {
			_formData.solicitorSignDate = format(new Date(), 'MMMM dd, yyyy');
		}

		for (let i = 0; i <= filteredMatterClients.length; i++) {
			const index = i + 1;
			const client =
				filteredMatterClients[i]?.type === ClientType.CORPORATION
					? getSignatories([filteredMatterClients[i]])[0]
					: (filteredMatterClients[i] as PersonMatterClientWithAssociations);

			const primaryClientIdentification = getIdentification(
				client?.identification?.[IdentificationPriority.PRIMARY],
				business?.defaultSettings?.shortDateFormat,
				business?.defaultSettings?.longDateFormat,
			);

			const secondaryClientIdentification = getIdentification(
				client?.identification?.[IdentificationPriority.SECONDARY],
				business?.defaultSettings?.shortDateFormat,
				business?.defaultSettings?.longDateFormat,
			);

			const secondaryIdType = client?.identification?.[
				IdentificationPriority.SECONDARY
			]?.type as CloserIdentificationTypes;

			if (
				primaryClientIdentification?.displayText &&
				!isSet(_formData?.[`mortgagor${index}Verification1IdType`])
			) {
				_formData[`mortgagor${index}Verification1IdType`] =
					primaryClientIdentification.displayText;
			}

			if (
				primaryClientIdentification?.nameOnId &&
				!isSet(_formData?.[`mortgagor${index}Verification1ClientName`])
			) {
				_formData[`mortgagor${index}Verification1ClientName`] =
					primaryClientIdentification.nameOnId;
			}

			if (
				client?.dateOfBirth &&
				!isSet(_formData?.[`mortgagor${index}Verification1Dob`])
			) {
				_formData[`mortgagor${index}Verification1Dob`] = DateToShortDate(
					client.dateOfBirth,
					business?.defaultSettings?.shortDateFormat,
				);
			}

			if (
				primaryClientIdentification?.number &&
				!isSet(_formData?.[`mortgagor${index}Verification1IdNumber`])
			) {
				_formData[`mortgagor${index}Verification1IdNumber`] =
					primaryClientIdentification.number;
			}

			if (
				(primaryClientIdentification?.jurisdiction ||
					primaryClientIdentification?.country) &&
				!isSet(_formData?.[`mortgagor${index}Verification1IssuancePlace`])
			) {
				_formData[`mortgagor${index}Verification1IssuancePlace`] = [
					primaryClientIdentification?.jurisdiction,
					primaryClientIdentification?.country,
				]
					.filter((x) => x)
					.join(', ');
			}

			if (
				primaryClientIdentification?.expirationDate &&
				!isSet(_formData?.[`mortgagor${index}Verification1ExpiryDate`])
			) {
				_formData[`mortgagor${index}Verification1ExpiryDate`] =
					primaryClientIdentification.expirationDate;
			}

			if (
				primaryClientIdentification?.dateOfVerification &&
				!isSet(_formData?.[`mortgagor${index}SolicitorVerifiedDate`])
			) {
				_formData[`mortgagor${index}SolicitorVerifiedDate`] =
					primaryClientIdentification.dateOfVerification;
			}

			if (
				primaryClientIdentification?.displayText &&
				!isSet(_formData?.[`mortgagor${index}Verification2AIdType`])
			) {
				_formData[`mortgagor${index}Verification2AIdType`] =
					secondaryClientIdentification.displayText;
			}

			/* 2B */

			if (this.SecondaryIdTypesToDetect.includes(secondaryIdType)) {
				if (
					secondaryClientIdentification?.displayText &&
					!isSet(_formData?.[`mortgagor${index}Verification2BAccountType`])
				) {
					_formData[`mortgagor${index}Verification2BAccountType`] =
						secondaryClientIdentification.displayText;
				}

				if (
					secondaryClientIdentification?.nameOnId &&
					!isSet(_formData?.[`mortgagor${index}Verification2BClientName`])
				) {
					_formData[`mortgagor${index}Verification2BClientName`] =
						secondaryClientIdentification.nameOnId;
				}

				if (
					client?.dateOfBirth &&
					!isSet(_formData?.[`mortgagor${index}Verification2BDob`])
				) {
					_formData[`mortgagor${index}Verification2BDob`] = DateToShortDate(
						client.dateOfBirth,
						business?.defaultSettings?.shortDateFormat,
					);
				}

				if (
					secondaryClientIdentification?.number &&
					!isSet(_formData?.[`mortgagor${index}Verification2BIdNumber`])
				) {
					_formData[`mortgagor${index}Verification2BIdNumber`] =
						secondaryClientIdentification.number;
				}

				if (
					(secondaryClientIdentification?.jurisdiction ||
						secondaryClientIdentification?.country) &&
					!isSet(_formData?.[`mortgagor${index}Verification2BIssuancePlace`])
				) {
					_formData[`mortgagor${index}Verification2BIssuancePlace`] = [
						secondaryClientIdentification?.jurisdiction,
						secondaryClientIdentification?.country,
					]
						.filter((x) => x)
						.join(', ');
				}

				if (
					secondaryClientIdentification?.expirationDate &&
					!isSet(_formData?.[`mortgagor${index}Verification2BExpiryDate`])
				) {
					_formData[`mortgagor${index}Verification2BExpiryDate`] =
						secondaryClientIdentification.expirationDate;
				}
			} else {
				if (
					secondaryClientIdentification?.displayText &&
					!isSet(_formData?.[`mortgagor${index}Verification2AIdType`])
				) {
					_formData[`mortgagor${index}Verification2AIdType`] =
						secondaryClientIdentification.displayText;
				}

				/* 2A */
				if (
					secondaryClientIdentification?.nameOnId &&
					!isSet(_formData?.[`mortgagor${index}Verification2AClientName`])
				) {
					_formData[`mortgagor${index}Verification2AClientName`] =
						secondaryClientIdentification.nameOnId;
				}

				if (
					client?.dateOfBirth &&
					!isSet(_formData?.[`mortgagor${index}Verification2ADob`])
				) {
					_formData[`mortgagor${index}Verification2ADob`] = DateToShortDate(
						client.dateOfBirth,
						business?.defaultSettings?.shortDateFormat,
					);
				}

				if (
					secondaryClientIdentification?.number &&
					!isSet(_formData?.[`mortgagor${index}Verification2AIdNumber`])
				) {
					_formData[`mortgagor${index}Verification2AIdNumber`] =
						secondaryClientIdentification.number;
				}

				if (
					(secondaryClientIdentification?.jurisdiction ||
						secondaryClientIdentification?.country) &&
					!isSet(_formData?.[`mortgagor${index}Verification2AIssuancePlace`])
				) {
					_formData[`mortgagor${index}Verification2AIssuancePlace`] = [
						secondaryClientIdentification?.jurisdiction,
						secondaryClientIdentification?.country,
					]
						.filter((x) => x)
						.join(', ');
				}

				if (
					secondaryClientIdentification?.expirationDate &&
					!isSet(_formData?.[`mortgagor${index}Verification2AExpiryDate`])
				) {
					_formData[`mortgagor${index}Verification2AExpiryDate`] =
						secondaryClientIdentification.expirationDate;
				}
			}
		}

		return _formData;
	}

	MapValues(): Partial<TD_NON_FACE_TO_FACE_ID> {
		return {};
	}
}

const Mappings: TD_NON_FACE_TO_FACE_ID = {
	fax: 'Fax',
	email: 'Email',
	solicitorTelephone: 'Solicitor_Telephone',
	solicitorFax: 'Solicitor_Fax',
	solicitorEmail: 'Solicitor_Email',
	date: 'Date',
	flexlineAccountNumber: 'Flexline_Account_Number',
	mortgagors: 'Mortgagors',
	guarantors: 'Guarantors',
	propertyAddress: 'Property_Address',
	propertyDescription: 'Property_Description',
	solicitorName: 'Solicitor_Name',
	solicitorSignDate: 'Solicitor_Sign_Date',
	attention: 'Attention',
	mortgagor1Verification1IdType: 'Mortgagor_1_Verification_1_ID_Type',
	mortgagor2Verification1IdType: 'Mortgagor_2_Verification_1_ID_Type',
	mortgagor3Verification1IdType: 'Mortgagor_3_Verification_1_ID_Type',
	mortgagor4Verification1IdType: 'Mortgagor_4_Verification_1_ID_Type',
	mortgagor4Verification1IdSource: 'Mortgagor_4_Verification_1_ID_source',
	mortgagor3Verification1IdSource: 'Mortgagor_3_Verification_1_ID_source',
	mortgagor2Verification1IdSource: 'Mortgagor_2_Verification_1_ID_source',
	mortgagor1Verification1IdSource: 'Mortgagor_1_Verification_1_ID_source',
	mortgagor2Verification1ClientName: 'Mortgagor_2_Verification_1_Client_Name',
	mortgagor3Verification1ClientName: 'Mortgagor_3_Verification_1_Client_Name',
	mortgagor1Verification1ClientName: 'Mortgagor_1_Verification_1_Client_Name',
	mortgagor4Verification1ClientName: 'Mortgagor_4_Verification_1_Client_Name',
	mortgagor2Verification1Dob: 'Mortgagor_2_Verification_1_DOB',
	mortgagor3Verification1Dob: 'Mortgagor_3_Verification_1_DOB',
	mortgagor1Verification1Dob: 'Mortgagor_1_Verification_1_DOB',
	mortgagor4Verification1Dob: 'Mortgagor_4_Verification_1_DOB',
	mortgagor2Verification1IdNumber: 'Mortgagor_2_Verification_1_ID_Number',
	mortgagor3Verification1IdNumber: 'Mortgagor_3_Verification_1_ID_Number',
	mortgagor1Verification1IdNumber: 'Mortgagor_1_Verification_1_ID_Number',
	mortgagor4Verification1IdNumber: 'Mortgagor_4_Verification_1_ID_Number',
	mortgagor2Verification1IssuancePlace:
		'Mortgagor_2_Verification_1_Issuance_Place',
	mortgagor3Verification1IssuancePlace:
		'Mortgagor_3_Verification_1_Issuance_Place',
	mortgagor1Verification1IssuancePlace:
		'Mortgagor_1_Verification_1_Issuance_Place',
	mortgagor4Verification1IssuancePlace:
		'Mortgagor_4_Verification_1_Issuance_Place',
	mortgagor2Verification1ExpiryDate: 'Mortgagor_2_Verification_1_Expiry_Date',
	mortgagor3Verification1ExpiryDate: 'Mortgagor_3_Verification_1_Expiry_Date',
	mortgagor1Verification1ExpiryDate: 'Mortgagor_1_Verification_1_Expiry_Date',
	mortgagor4Verification1ExpiryDate: 'Mortgagor_4_Verification_1_Expiry_Date',
	mortgagor2Verification1DobMatch: 'Mortgagor_2_Verification_1_DOB_Match',
	mortgagor3Verification1DobMatch: 'Mortgagor_3_Verification_1_DOB_Match',
	mortgagor1Verification1DobMatch: 'Mortgagor_1_Verification_1_DOB_Match',
	mortgagor4Verification1DobMatch: 'Mortgagor_4_Verification_1_DOB_Match',
	mortgagor2Verification2AIdType: 'Mortgagor_2_Verification_2a_ID_Type',
	mortgagor3Verification2AIdType: 'Mortgagor_3_Verification_2a_ID_Type',
	mortgagor1Verification2AIdType: 'Mortgagor_1_Verification_2a_ID_Type',
	mortgagor4Verification2AIdType: 'Mortgagor_4_Verification_2a_ID_Type',
	mortgagor2Verification2AIdSource: 'Mortgagor_2_Verification_2a_ID_Source',
	mortgagor3Verification2AIdSource: 'Mortgagor_3_Verification_2a_ID_Source',
	mortgagor1Verification2AIdSource: 'Mortgagor_1_Verification_2a_ID_Source',
	mortgagor4Verification2AIdSource: 'Mortgagor_4_Verification_2a_ID_Source',
	mortgagor2Verification2AClientName: 'Mortgagor_2_Verification_2a_Client_Name',
	mortgagor3Verification2AClientName: 'Mortgagor_3_Verification_2a_Client_Name',
	mortgagor1Verification2AClientName: 'Mortgagor_1_Verification_2a_Client_Name',
	mortgagor4Verification2AClientName: 'Mortgagor_4_Verification_2a_Client_Name',
	mortgagor1Verification2AAddress1: 'Mortgagor_1_Verification_2a_Address1',
	mortgagor1Verification2AAddress2: 'Mortgagor_1_Verification_2a_Address2',
	mortgagor2Verification2AAddress1: 'Mortgagor_2_Verification_2a_Address1',
	mortgagor2Verification2AAddress2: 'Mortgagor_2_Verification_2a_Address2',
	mortgagor3Verification2AAddress1: 'Mortgagor_3_Verification_2a_Address1',
	mortgagor3Verification2AAddress2: 'Mortgagor_3_Verification_2a_Address2',
	mortgagor4Verification2AAddress1: 'Mortgagor_4_Verification_2a_Address1',
	mortgagor4Verification2AAddress2: 'Mortgagor_4_Verification_2a_Address2',
	mortgagor2Verification2AIdNumber: 'Mortgagor_2_Verification_2a_ID_Number',
	mortgagor3Verification2AIdNumber: 'Mortgagor_3_Verification_2a_ID_Number',
	mortgagor1Verification2AIdNumber: 'Mortgagor_1_Verification_2a_ID_Number',
	mortgagor4Verification2AIdNumber: 'Mortgagor_4_Verification_2a_ID_Number',
	mortgagor2Verification2AIssuancePlace:
		'Mortgagor_2_Verification_2a_Issuance_Place',
	mortgagor3Verification2AIssuancePlace:
		'Mortgagor_3_Verification_2a_Issuance_Place',
	mortgagor1Verification2AIssuancePlace:
		'Mortgagor_1_Verification_2a_Issuance_Place',
	mortgagor4Verification2AIssuancePlace:
		'Mortgagor_4_Verification_2a_Issuance_Place',
	mortgagor2Verification2AExpiryDate: 'Mortgagor_2_Verification_2a_Expiry_Date',
	mortgagor3Verification2AExpiryDate: 'Mortgagor_3_Verification_2a_Expiry_Date',
	mortgagor1Verification2AExpiryDate: 'Mortgagor_1_Verification_2a_Expiry_Date',
	mortgagor4Verification2AExpiryDate: 'Mortgagor_4_Verification_2a_Expiry_Date',
	mortgagor2Verification2AAddressMatch:
		'Mortgagor_2_Verification_2a_Address_Match',
	mortgagor3Verification2AAddressMatch:
		'Mortgagor_3_Verification_2a_Address_Match',
	mortgagor1Verification2AAddressMatch:
		'Mortgagor_1_Verification_2a_Address_Match',
	mortgagor4Verification2AAddressMatch:
		'Mortgagor_4_Verification_2a_Address_Match',
	mortgagor2Verification2BAccountType:
		'Mortgagor_2_Verification_2b_Account_Type',
	mortgagor3Verification2BAccountType:
		'Mortgagor_3_Verification_2b_Account_Type',
	mortgagor1Verification2BAccountType:
		'Mortgagor_1_Verification_2b_Account_Type',
	mortgagor4Verification2BAccountType:
		'Mortgagor_4_Verification_2b_Account_Type',
	mortgagor2Verification2BOfiName: 'Mortgagor_2_Verification_2b_OFI_Name',
	mortgagor3Verification2BOfiName: 'Mortgagor_3_Verification_2b_OFI_Name',
	mortgagor1Verification2BOfiName: 'Mortgagor_1_Verification_2b_OFI_Name',
	mortgagor4Verification2BOfiName: 'Mortgagor_4_Verification_2b_OFI_Name',
	mortgagor2Verification2BAccountNumber:
		'Mortgagor_2_Verification_2b_Account_Number',
	mortgagor3Verification2BAccountNumber:
		'Mortgagor_3_Verification_2b_Account_Number',
	mortgagor1Verification2BAccountNumber:
		'Mortgagor_1_Verification_2b_Account_Number',
	mortgagor4Verification2BAccountNumber:
		'Mortgagor_4_Verification_2b_Account_Number',
	mortgagor2Verification2BStatementDate:
		'Mortgagor_2_Verification_2b_Statement_Date',
	mortgagor1Verification2BStatementDate:
		'Mortgagor_1_Verification_2b_Statement_Date',
	mortgagor4Verification2BStatementDate:
		'Mortgagor_4_Verification_2b_Statement_Date',
	mortgagor3Verification2BStatementDate:
		'Mortgagor_3_Verification_2b_Statement_Date',
	mortgagor2Verification2BClientName: 'Mortgagor_2_Verification_2b_Client_Name',
	mortgagor3Verification2BClientName: 'Mortgagor_3_Verification_2b_Client_Name',
	mortgagor1Verification2BClientName: 'Mortgagor_1_Verification_2b_Client_Name',
	mortgagor4Verification2BClientName: 'Mortgagor_4_Verification_2b_Client_Name',
	mortgagor1Verification2BAddress1: 'Mortgagor_1_Verification_2b_Address1',
	mortgagor1Verification2BAddress2: 'Mortgagor_1_Verification_2b_Address2',
	mortgagor2Verification2BAddress1: 'Mortgagor_2_Verification_2b_Address1',
	mortgagor2Verification2BAddress2: 'Mortgagor_2_Verification_2b_Address2',
	mortgagor4Verification2BAddress1: 'Mortgagor_4_Verification_2b_Address1',
	mortgagor4Verification2BAddress2: 'Mortgagor_4_Verification_2b_Address2',
	mortgagor3Verification2BAddress1: 'Mortgagor_3_Verification_2b_Address1',
	mortgagor3Verification2BAddress2: 'Mortgagor_3_Verification_2b_Address2',
	mortgagor2Verification2BAddressMatch:
		'Mortgagor_2_Verification_2b_Address_Match',
	mortgagor3Verification2BAddressMatch:
		'Mortgagor_3_Verification_2b_Address_Match',
	mortgagor1Verification2BAddressMatch:
		'Mortgagor_1_Verification_2b_Address_Match',
	mortgagor4Verification2BAddressMatch:
		'Mortgagor_4_Verification_2b_Address_Match',
	mortgagor2SolicitorVerifiedDate: 'Mortgagor_2_Solicitor_Verified_Date',
	mortgagor3SolicitorVerifiedDate: 'Mortgagor_3_Solicitor_Verified_Date',
	mortgagor1SolicitorVerifiedDate: 'Mortgagor_1_Solicitor_Verified_Date',
	mortgagor4SolicitorVerifiedDate: 'Mortgagor_4_Solicitor_Verified_Date',
};

export interface TD_NON_FACE_TO_FACE_ID {
	fax: string;
	email: string;
	solicitorTelephone: string;
	solicitorFax: string;
	solicitorEmail: string;
	date: string;
	flexlineAccountNumber: string;
	mortgagors: string;
	guarantors: string;
	propertyAddress: string;
	propertyDescription: string;
	solicitorName: string;
	solicitorSignDate: string;
	attention: string;
	mortgagor1Verification1IdType: string;
	mortgagor2Verification1IdType: string;
	mortgagor3Verification1IdType: string;
	mortgagor4Verification1IdType: string;
	mortgagor4Verification1IdSource: string;
	mortgagor3Verification1IdSource: string;
	mortgagor2Verification1IdSource: string;
	mortgagor1Verification1IdSource: string;
	mortgagor2Verification1ClientName: string;
	mortgagor3Verification1ClientName: string;
	mortgagor1Verification1ClientName: string;
	mortgagor4Verification1ClientName: string;
	mortgagor2Verification1Dob: string;
	mortgagor3Verification1Dob: string;
	mortgagor1Verification1Dob: string;
	mortgagor4Verification1Dob: string;
	mortgagor2Verification1IdNumber: string;
	mortgagor3Verification1IdNumber: string;
	mortgagor1Verification1IdNumber: string;
	mortgagor4Verification1IdNumber: string;
	mortgagor2Verification1IssuancePlace: string;
	mortgagor3Verification1IssuancePlace: string;
	mortgagor1Verification1IssuancePlace: string;
	mortgagor4Verification1IssuancePlace: string;
	mortgagor2Verification1ExpiryDate: string;
	mortgagor3Verification1ExpiryDate: string;
	mortgagor1Verification1ExpiryDate: string;
	mortgagor4Verification1ExpiryDate: string;
	mortgagor2Verification1DobMatch: string;
	mortgagor3Verification1DobMatch: string;
	mortgagor1Verification1DobMatch: string;
	mortgagor4Verification1DobMatch: string;
	mortgagor2Verification2AIdType: string;
	mortgagor3Verification2AIdType: string;
	mortgagor1Verification2AIdType: string;
	mortgagor4Verification2AIdType: string;
	mortgagor2Verification2AIdSource: string;
	mortgagor3Verification2AIdSource: string;
	mortgagor1Verification2AIdSource: string;
	mortgagor4Verification2AIdSource: string;
	mortgagor2Verification2AClientName: string;
	mortgagor3Verification2AClientName: string;
	mortgagor1Verification2AClientName: string;
	mortgagor4Verification2AClientName: string;
	mortgagor1Verification2AAddress1: string;
	mortgagor1Verification2AAddress2: string;
	mortgagor2Verification2AAddress1: string;
	mortgagor2Verification2AAddress2: string;
	mortgagor3Verification2AAddress1: string;
	mortgagor3Verification2AAddress2: string;
	mortgagor4Verification2AAddress1: string;
	mortgagor4Verification2AAddress2: string;
	mortgagor2Verification2AIdNumber: string;
	mortgagor3Verification2AIdNumber: string;
	mortgagor1Verification2AIdNumber: string;
	mortgagor4Verification2AIdNumber: string;
	mortgagor2Verification2AIssuancePlace: string;
	mortgagor3Verification2AIssuancePlace: string;
	mortgagor1Verification2AIssuancePlace: string;
	mortgagor4Verification2AIssuancePlace: string;
	mortgagor2Verification2AExpiryDate: string;
	mortgagor3Verification2AExpiryDate: string;
	mortgagor1Verification2AExpiryDate: string;
	mortgagor4Verification2AExpiryDate: string;
	mortgagor2Verification2AAddressMatch: string;
	mortgagor3Verification2AAddressMatch: string;
	mortgagor1Verification2AAddressMatch: string;
	mortgagor4Verification2AAddressMatch: string;
	mortgagor2Verification2BAccountType: string;
	mortgagor3Verification2BAccountType: string;
	mortgagor1Verification2BAccountType: string;
	mortgagor4Verification2BAccountType: string;
	mortgagor2Verification2BOfiName: string;
	mortgagor3Verification2BOfiName: string;
	mortgagor1Verification2BOfiName: string;
	mortgagor4Verification2BOfiName: string;
	mortgagor2Verification2BAccountNumber: string;
	mortgagor3Verification2BAccountNumber: string;
	mortgagor1Verification2BAccountNumber: string;
	mortgagor4Verification2BAccountNumber: string;
	mortgagor2Verification2BStatementDate: string;
	mortgagor1Verification2BStatementDate: string;
	mortgagor4Verification2BStatementDate: string;
	mortgagor3Verification2BStatementDate: string;
	mortgagor2Verification2BClientName: string;
	mortgagor3Verification2BClientName: string;
	mortgagor1Verification2BClientName: string;
	mortgagor4Verification2BClientName: string;
	mortgagor1Verification2BAddress1: string;
	mortgagor1Verification2BAddress2: string;
	mortgagor2Verification2BAddress1: string;
	mortgagor2Verification2BAddress2: string;
	mortgagor4Verification2BAddress1: string;
	mortgagor4Verification2BAddress2: string;
	mortgagor3Verification2BAddress1: string;
	mortgagor3Verification2BAddress2: string;
	mortgagor2Verification2BAddressMatch: string;
	mortgagor3Verification2BAddressMatch: string;
	mortgagor1Verification2BAddressMatch: string;
	mortgagor4Verification2BAddressMatch: string;
	mortgagor2SolicitorVerifiedDate: string;
	mortgagor3SolicitorVerifiedDate: string;
	mortgagor1SolicitorVerifiedDate: string;
	mortgagor4SolicitorVerifiedDate: string;
}
